import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Button,
  Space,
  Input,
  message,
  Typography,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { ruleService } from '../services/rule';
import RuleEditModal from './RuleEditModal';
import { handleApiError } from '../utils/errorHandler';

const { Text } = Typography;

// 规则配置模式类型
export type RuleConfigMode = 'create' | 'edit' | 'view';

// 预定义的规则类型
const PREDEFINED_RULES = [
  { type: 'url_referer', name: '访客来源' },
  { type: 'ip_blacklist', name: 'IP黑白名单' },
  { type: 'crawler', name: '爬虫拦截' },
  { type: 'network', name: '网络安全检测' },
  { type: 'geography', name: '国家地区拦截' },
  { type: 'device', name: '设备检测' },
  { type: 'language', name: '访客语言拦截' },
  { type: 'asn', name: '网络归属控制' },
  { type: 'utm', name: 'UTM参数拦截' },
  { type: 'advertising', name: '广告平台拦截' },
];

export interface RuleConfigItem {
  id?: number;
  type: string;
  name: string;
  isActive?: boolean;
  config?: any;
}

interface RuleConfigModalProps {
  visible: boolean;
  mode?: 'create' | 'edit' | 'view';
  title?: string;
  rules?: RuleConfigItem[];
  ruleId?: number | null;
  linkId?: number | null;
  ruleName?: string;
  onOk?: () => void;
  onSave?: (updatedRules: RuleConfigItem[], updatedRuleName?: string) => void;
  onCancel: () => void;
}

const RuleConfigModal: React.FC<RuleConfigModalProps> = ({
  visible,
  mode = 'create',
  title,
  rules: externalRules,
  ruleId,
  linkId,
  ruleName,
  onOk,
  onSave,
  onCancel,
}) => {
  const [rules, setRules] = useState<RuleConfigItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState<any>(null);
  const [editingRuleIndex, setEditingRuleIndex] = useState<number>(-1);
  
  // 规则名称编辑状态
  const [editingName, setEditingName] = useState<string>(ruleName || '');
  const [isEditingName, setIsEditingName] = useState<boolean>(() => !ruleName);
  
  // 判断是否为只读模式
  const readonly = mode === 'view';

  // 初始化规则名称
useEffect(() => {
  if (visible) {
    if (ruleName) {
      setEditingName(ruleName);
      setIsEditingName(false);
    } else {
      setEditingName('');
      setIsEditingName(true);
    }
  }
}, [visible, ruleName]);

  // 开始编辑规则名称
  const handleStartEditName = () => {
    setIsEditingName(true);
  };

  // 取消编辑规则名称
  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditingName(ruleName || '');
  };

  useEffect(() => {
    if (visible) {
      if (externalRules) {
        // 使用外部传入的规则
        setRules(externalRules);
      } else if (ruleId) {
        loadRuleConfig();
      } else {
        // 新建规则，初始化空配置
        const initial = PREDEFINED_RULES.map(r => ({ ...r, config: {} }));
        setRules(initial);
      }
    }
  }, [visible, ruleId, externalRules]);

  const loadRuleConfig = async () => {
    if (!ruleId) return;
    
    setLoading(true);
    try {
      const resp = await ruleService.getRuleDetail(ruleId);
      const ruleConfig = (resp && (resp as any).rule_config) || {};
	const initial = PREDEFINED_RULES.map(r => {
		const cfg = ruleConfig[r.type] || {};
		return { ...r, config: cfg };
	});
      setRules(initial);
    } catch (e) {
      message.error('加载规则配置失败');
      const initial = PREDEFINED_RULES.map(r => ({ ...r, config: {} }));
      setRules(initial);
    } finally {
      setLoading(false);
    }
  };

  const handleEditRule = (index: number) => {
    setEditingRuleIndex(index);
    setEditingRule(rules[index] as any);
    setEditModalVisible(true);
  };

  const handleEditRuleOk = (updatedRule: any) => {
    if (editingRuleIndex >= 0) {
      const newRules = [...rules];
      newRules[editingRuleIndex] = { ...newRules[editingRuleIndex], config: updatedRule.config || {} };
      setRules(newRules);
    }
    setEditModalVisible(false);
    setEditingRule(null);
    setEditingRuleIndex(-1);
  };

  const handleEditRuleCancel = () => {
    setEditModalVisible(false);
    setEditingRule(null);
    setEditingRuleIndex(-1);
  };

  const handleSave = async () => {
    try {
      const trimmedName = editingName.trim() || ruleName?.trim() || '';
      if (!trimmedName) {
        message.warning('请先填写规则名称');
        return;
      }

      if (onSave) {
        // 使用外部传入的保存函数，传递规则名称
        onSave(rules, trimmedName);
      } else {
        // 内部保存逻辑
        const fullRuleConfig: any = {};
		for (const r of rules) {
			// 直接使用配置内容（包含 enabled 等字段）
			fullRuleConfig[r.type] = r.config || {};
		}

        if (ruleId) {
          // 更新现有规则
          const updateData: any = { id: ruleId, rule_config: fullRuleConfig };
          
          // 如果规则名称有修改，同时更新名称
          if (trimmedName && trimmedName !== ruleName) {
            updateData.name = trimmedName;
          }
          
          await ruleService.updateRule(updateData);
          message.success('规则配置已更新');
        } else {
          // 创建新规则
          await ruleService.createRule({ name: trimmedName, rule_config: fullRuleConfig });
          message.success('规则创建成功');
        }
        
        if (onOk) {
          onOk();
        }
      }
    } catch (error) {
      console.error('保存规则失败:', error);
      handleApiError(error, '保存失败');
    }
  };

  // 检查规则是否有配置
  const hasConfig = (config: any) => {
    if (!config || typeof config !== 'object') return false;
    
    // 检查是否有实际配置内容（非空数组或非空对象）
    for (const key in config) {
      const value = config[key];
      if (Array.isArray(value) && value.length > 0) return true;
      if (typeof value === 'boolean' && value === true) return true;
      if (typeof value === 'string' && value.trim() !== '') return true;
      if (typeof value === 'object' && value !== null && Object.keys(value).length > 0) return true;
    }
    
    return false;
  };

  const columns = [
    {
      title: '规则类型',
      dataIndex: 'type',
      key: 'type',
      render: (type: string, record: RuleConfigItem) => (
        <div>
          <div>{record.name}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {type}
          </Text>
        </div>
      ),
    },
    {
      title: '规则名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '配置状态',
      key: 'config_status',
      render: (_: any, record: RuleConfigItem) => {
        const configured = hasConfig(record.config);
        return (
          <Text type={configured ? 'success' : 'secondary'}>
            {configured ? '已配置' : '未配置'}
          </Text>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: RuleConfigItem, index: number) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            onClick={() => handleEditRule(index)}
          >
            {readonly ? '查看' : '编辑'}
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={title || `${readonly ? '查看' : '规则配置'} ${ruleName ? `- ${ruleName}` : ''}`}
        open={visible}
        onOk={readonly ? undefined : handleSave}
        onCancel={onCancel}
        width={800}
        okText={readonly ? undefined : "保存配置"}
        cancelText="关闭"
        footer={readonly ? [
          <Button key="close" onClick={onCancel}>
            关闭
          </Button>
        ] : undefined}
      >
        {/* 规则名称编辑区域 */}
        {!readonly && (
          <div style={{ 
            marginBottom: 16, 
            padding: 16, 
            backgroundColor: '#fafafa', 
            borderRadius: 6,
            border: '1px solid #f0f0f0'
          }}>
            <div style={{ marginBottom: 8 }}>
              <Text strong>规则名称：</Text>
            </div>
            {isEditingName ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Input
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  autoFocus
                  style={{ flex: 1 }}
                  placeholder="请输入规则名称"
                />
                {ruleName && (
                  <Button
                    type="text"
                    size="small"
                    onClick={handleCancelEditName}
                    style={{ color: '#ff4d4f' }}
                  >
                    取消
                  </Button>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Text style={{ flex: 1, fontSize: '16px' }}>{editingName}</Text>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={handleStartEditName}
                  style={{ color: '#1890ff' }}
                />
              </div>
            )}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">
            {readonly 
              ? '查看各类拦截规则配置，只有配置了具体内容的规则才会生效' 
              : '配置各类拦截规则，只有配置了具体内容的规则才会生效'
            }
          </Text>
        </div>
        
        <Table
          dataSource={rules}
          columns={columns}
          rowKey="type"
          loading={loading}
          pagination={false}
          size="small"
        />

        <div style={{ marginTop: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
          <Text strong>{readonly ? '规则说明：' : '配置说明：'}</Text>
          <ul style={{ marginTop: 8, marginBottom: 0, fontSize: '12px' }}>
            <li>只有配置了具体内容的规则才会生效，无需手动开启/关闭</li>
            <li>{readonly ? '点击"查看"按钮查看具体的拦截条件配置' : '点击"编辑"按钮配置具体的拦截条件'}</li>
		<li>支持9种拦截类型：访客来源、IP黑白名单、爬虫拦截、网络安全检测、国家地区拦截、设备检测、访客语言拦截、网络归属控制、UTM参数拦截</li>
            <li>规则按优先级依次检查，任一规则匹配即拦截</li>
            {readonly && <li style={{ color: '#1890ff' }}>当前为查看模式，可以查看规则配置详情，如需编辑规则请前往"拦截规则管理"页面</li>}
          </ul>
        </div>
      </Modal>

      {/* 编辑单个规则的弹窗 */}
      <RuleEditModal
        visible={editModalVisible}
        rule={editingRule}
        readonly={readonly}
        onOk={handleEditRuleOk}
        onCancel={handleEditRuleCancel}
      />
    </>
  );
};

export default RuleConfigModal;